import type { FeatureFlagService } from '@affine/core/modules/feature-flag';
import type { PeekViewService } from '@affine/core/modules/peek-view';
import type { AppThemeService } from '@affine/core/modules/theme';
import type { CopilotChatHistoryFragment } from '@affine/graphql';
import { I18n } from '@affine/i18n';
import { WithDisposable } from '@blocksuite/affine/global/lit';
import { isInsidePageEditor } from '@blocksuite/affine/shared/utils';
import {
  type BlockStdScope,
  type EditorHost,
  ShadowlessElement,
} from '@blocksuite/affine/std';
import type { ExtensionType } from '@blocksuite/affine/store';
import {
  type NotificationService,
  ThemeProvider,
} from '@blocksuite/affine-shared/services';
import { streamLainDisclosureStyles } from '@blocksuite/affine-shared/utils';
import { CloseIcon, CodeBlockIcon } from '@blocksuite/icons/lit';
import type { Signal } from '@preact/signals-core';
import { css, html, nothing, unsafeCSS } from 'lit';
import { property } from 'lit/decorators.js';

import type { WorkspaceDocSyncEngine } from '../../../../modules/streamlain/stream-objects';
import { assistantSourceManifestToPillItems } from '../../_common/assistant-source-pills';
import {
  type AssistantTurnPayload,
  buildReusableAssistantTurnMarkdown,
} from '../../_common/assistant-turn-payload';
import {
  EdgelessEditorActions,
  PageEditorActions,
} from '../../_common/chat-actions-handle';
import type { DocDisplayConfig } from '../../components/ai-chat-chips';
import type { CodeSelectionAttachment } from '../../components/ai-chat-content/type';
import {
  type ChatMessage,
  type ChatStatus,
  isChatMessage,
  type StreamObject,
  type ToolMessageData,
  toolResultToStreamObject,
} from '../../components/ai-chat-messages';
import { renderAssistantIdentity } from '../../components/ai-message-content/assistant-identity';
import {
  docSearchSourceStyles,
  renderSourceList,
} from '../../components/ai-tools/doc-search-source';
import { AIChatErrorRenderer } from '../../messages/error';
import { type AIError } from '../../provider';
import {
  aiChatBadgeTypography,
  aiChatControlTypography,
  aiChatIconSize,
  aiChatMetaTypography,
  aiChatStatusTypography,
} from '../../styles/ai-chat-typography';
import { sanitizeChatMessageContentForDisplay } from '../../utils/blob-url-resolver';
import { dispatchCodeSelectionProposal } from '../../utils/code-selection-context';
import { parseLocalizedCopy } from '../../utils/localized-copy';
import {
  localizedToolName,
  localizedToolText,
  mergeStreamContent,
  reconcilePersistedToolResults,
  shouldShowAssistantProgressPlaceholder,
} from '../../utils/stream-objects';
import type { ConfirmationMessage } from './confirmation';

interface MarkdownCodeBlock {
  readonly language: string;
  readonly code: string;
}

const LANGUAGE_ALIASES: Record<CodeSelectionAttachment['language'], string[]> =
  {
    javascript: ['javascript', 'js'],
    python: ['python', 'py'],
    json: ['json'],
    css: ['css'],
    text: ['text', 'plain', 'plaintext', ''],
  };

const SOURCE_TOOL_RESULT_NAMES = new Set([
  'doc_semantic_search',
  'doc_keyword_search',
  'doc_read',
  'database_read',
  'blob_read',
  'mail_search',
  'mail_list_drafts',
  'mail_get_message',
  'mail_summarize',
  'mail_draft_reply',
  'mail_read_aloud_plan',
  'web_search',
  'web_crawl',
  'doc_create',
  'doc_compose',
]);

function isSourceToolResultStreamObject(streamObject: StreamObject): boolean {
  return (
    streamObject.type === 'tool-result' &&
    SOURCE_TOOL_RESULT_NAMES.has(streamObject.toolName)
  );
}

function extractMarkdownCodeBlocks(markdown: string): MarkdownCodeBlock[] {
  const blocks: MarkdownCodeBlock[] = [];
  const fencePattern = /```([^\n`]*)\n([\s\S]*?)```/g;
  let match: RegExpExecArray | null;
  while ((match = fencePattern.exec(markdown)) !== null) {
    const language = match[1]?.trim().toLowerCase() ?? '';
    const code = match[2] ?? '';
    if (code.trim()) blocks.push({ language, code });
  }
  return blocks;
}

function extractCodeProposal(
  markdown: string,
  language: CodeSelectionAttachment['language']
): string | null {
  const blocks = extractMarkdownCodeBlocks(markdown);
  if (!blocks.length) return null;

  const aliases = LANGUAGE_ALIASES[language];
  const matching = blocks.find(block => aliases.includes(block.language));
  return matching?.code ?? blocks[0].code;
}

export class ChatMessageAssistant extends WithDisposable(ShadowlessElement) {
  static override styles = css`
    ${unsafeCSS(streamLainDisclosureStyles)}
    ${unsafeCSS(docSearchSourceStyles)}

    .message-info {
      ${aiChatMetaTypography}
      color: var(--streamlain-placeholder-color);
    }

    /* sl-canvas2-8.31 Phase 6: Tool result cards use unified tool-result-card / tool-call-failed components */

    /* sl-k8wk.2.2: Low-balance warning banner (Owner/Admin only) */
    .low-balance-banner {
      ${aiChatStatusTypography}
      display: flex;
      align-items: center;
      gap: 8px;
      margin-top: 8px;
      padding: 8px 12px;
      border-radius: 8px;
      background: var(
        --streamlain-v2-layer-background-warningLight,
        rgba(255, 165, 0, 0.08)
      );
      border: 1px solid
        var(--streamlain-v2-layer-border-warning, rgba(255, 165, 0, 0.2));
      color: var(--streamlain-text-primary-color);
    }

    .low-balance-banner-text {
      flex: 1;
    }

    .low-balance-banner-action {
      ${aiChatControlTypography}
      flex-shrink: 0;
      padding: 2px 8px;
      border-radius: 4px;
      background: transparent;
      border: 1px solid var(--streamlain-border-color);
      color: var(--streamlain-text-primary-color);
      cursor: pointer;
      white-space: nowrap;
    }

    .low-balance-banner-action:hover {
      background: var(
        --streamlain-v2-layer-background-hoverOverlay,
        rgba(0, 0, 0, 0.04)
      );
    }

    .low-balance-banner-dismiss {
      flex-shrink: 0;
      padding: 2px;
      background: transparent;
      border: none;
      color: var(--streamlain-text-secondary-color);
      cursor: pointer;
      width: ${aiChatIconSize.small};
      height: ${aiChatIconSize.small};
    }

    .low-balance-banner-dismiss svg {
      width: ${aiChatIconSize.small};
      height: ${aiChatIconSize.small};
    }

    .code-proposal-action {
      ${aiChatControlTypography}
      display: inline-flex;
      align-items: center;
      gap: 6px;
      margin-top: 8px;
      margin-bottom: 4px;
      border: 1px solid var(--streamlain-border-color);
      border-radius: 8px;
      background: var(--streamlain-background-secondary-color);
      color: var(--streamlain-text-primary-color);
      cursor: pointer;
      font-weight: 600;
      padding: 6px 10px;
    }

    .code-proposal-action:hover {
      background: var(--streamlain-hover-color);
    }

    .routing-badge {
      ${aiChatBadgeTypography}
      display: inline-flex;
      align-items: center;
      gap: 4px;
      margin-bottom: 4px;
      padding: 2px 8px;
      border-radius: 10px;
      background: var(--streamlain-v2-layer-background-hoverOverlay);
      color: var(--streamlain-text-secondary-color);
    }
  `;

  @property({ attribute: false })
  accessor host: EditorHost | null | undefined;

  @property({ attribute: false })
  accessor std: BlockStdScope | null | undefined;

  @property({ attribute: false })
  accessor item!: ChatMessage;

  @property({ attribute: false })
  accessor isLast: boolean = false;

  @property({ attribute: 'data-status', reflect: true })
  accessor status: ChatStatus = 'idle';

  @property({ attribute: false })
  accessor error: AIError | null = null;

  @property({ attribute: false })
  accessor extensions: ExtensionType[] = [];

  @property({ attribute: false })
  accessor affineFeatureFlagService!: FeatureFlagService;

  @property({ attribute: false })
  accessor affineThemeService: AppThemeService | undefined;

  @property({ attribute: false })
  accessor session!: CopilotChatHistoryFragment | null | undefined;

  @property({ attribute: false })
  accessor workspaceId!: string;

  @property({ attribute: false })
  accessor retry: () => void = () => {};

  @property({ attribute: 'data-testid', reflect: true })
  accessor testId = 'chat-message-assistant';

  @property({ attribute: false })
  accessor width: Signal<number | undefined> | undefined;

  @property({ attribute: false })
  accessor notificationService: NotificationService | undefined;

  @property({ attribute: false })
  accessor independentMode: boolean | undefined;

  @property({ attribute: false })
  accessor docDisplayService!: DocDisplayConfig;

  @property({ attribute: false })
  accessor codeSelections: CodeSelectionAttachment[] = [];

  @property({ attribute: false })
  accessor peekViewService!: PeekViewService;

  @property({ attribute: false })
  accessor onOpenDoc!: (docId: string, sessionId?: string) => void;

  @property({ attribute: false })
  accessor workspaceDocSyncEngine: WorkspaceDocSyncEngine | null | undefined;

  // sl-k8wk.2.2: Callback to open AI settings (for "Credits verwalten" button)
  @property({ attribute: false })
  accessor onOpenSettings: (() => void) | undefined;

  @property({ attribute: false })
  accessor readOnlyPresentation = false;

  private get themeSignal() {
    return (
      this.affineThemeService?.appTheme.themeSignal ??
      this.host?.std.getOptional(ThemeProvider)?.app$
    );
  }

  get state() {
    const { isLast, status } = this;
    return isLast
      ? status !== 'loading' && status !== 'transmitting'
        ? 'finished'
        : 'generating'
      : 'finished';
  }

  renderHeader() {
    const isWithDocs =
      'content' in this.item &&
      this.item.content &&
      this.item.content.includes('[^') &&
      /\[\^\d+\]:{"type":"doc","docId":"[^"]+"}/.test(this.item.content);

    return renderAssistantIdentity(
      this.status,
      isWithDocs
        ? html`<span class="message-info">with your docs</span>`
        : nothing
    );
  }

  /**
   * sl-55fc.3: Reconstruct streamObjects from toolData.toolResults + content.
   * Ensures history uses the SAME renderStreamObjects() path as live streaming.
   */
  private reconstructStreamObjects(item: ChatMessage): StreamObject[] {
    const objects: StreamObject[] = [];
    const content = sanitizeChatMessageContentForDisplay(item.content);

    const results = item.toolData?.toolResults;
    if (results?.length) {
      objects.push(...results.map(toolResultToStreamObject));
    }

    if (content.trim()) {
      objects.push({ type: 'text-delta' as const, textDelta: content });
    }

    // sl-tkri.3.1: Reconstruct usage badge from persisted toolData.creditsUsed
    const td = item.toolData as
      | { creditsUsed?: number; model?: string }
      | undefined;
    if (td?.creditsUsed !== undefined) {
      objects.push({
        type: 'usage' as const,
        creditsUsed: td.creditsUsed,
        model: td.model ?? '',
      });
    }

    return objects;
  }

  private sanitizeStreamObjects(streamObjects: StreamObject[]): StreamObject[] {
    let changed = false;
    const sanitized = streamObjects.map(streamObject => {
      if (
        (streamObject.type === 'text-delta' ||
          streamObject.type === 'reasoning') &&
        streamObject.textDelta
      ) {
        const textDelta = sanitizeChatMessageContentForDisplay(
          streamObject.textDelta
        );
        if (textDelta !== streamObject.textDelta) {
          changed = true;
          return { ...streamObject, textDelta };
        }
      }
      return streamObject;
    });

    return changed ? sanitized : streamObjects;
  }

  renderContent() {
    const { host, item, isLast, status, error } = this;
    const shouldRenderError = isLast && status === 'error' && !!error;

    const { messageType, toolData } = item;

    // Render tool-approval-request as confirmation card
    if (messageType === 'tool_approval_request' && toolData) {
      return this.renderToolApprovalRequest(toolData);
    }

    // Skip tool_call and tool_result messages in history —
    // their results are consolidated in the text message's toolData.toolResults
    if (messageType === 'tool_call' || messageType === 'tool_result') {
      return nothing;
    }

    // sl-55fc: Single rendering path for live and history
    // sl-oiya: When live streamObjects exist but contain only metadata (memory-recall,
    // knowledge-graph-context, usage, meta, warning) without any text-delta,
    // reconstruct from content so the text is not lost.
    const content = sanitizeChatMessageContentForDisplay(item.content);
    let effectiveStreamObjects: StreamObject[];
    if (item.streamObjects?.length) {
      const hasTextContent = item.streamObjects.some(
        so => so.type === 'text-delta' || so.type === 'reasoning'
      );
      if (hasTextContent) {
        effectiveStreamObjects = this.sanitizeStreamObjects(
          reconcilePersistedToolResults(
            item.streamObjects,
            item.toolData?.toolResults
          )
        );
      } else {
        // streamObjects are metadata-only (tool cards, memory events) — merge with content
        effectiveStreamObjects = [
          ...reconcilePersistedToolResults(
            item.streamObjects,
            item.toolData?.toolResults
          ),
          ...(content?.trim()
            ? [{ type: 'text-delta' as const, textDelta: content }]
            : []),
        ];
      }
    } else {
      effectiveStreamObjects = this.reconstructStreamObjects(item);
    }

    // sl-k8wk.2.2: Extract low-balance meta from stream objects
    const metaObject = effectiveStreamObjects.find(
      so => so.type === 'meta' && so.lowBalance
    );
    // sl-ufq6.7.1: Extract budget warning from stream objects
    const warningObject = effectiveStreamObjects.find(
      so => so.type === 'warning'
    );
    // sl-sf9o: Extract routing telemetry from stream objects
    const routingObject = effectiveStreamObjects.find(
      so => so.type === 'routing'
    );
    // Filter out usage, meta, warning, and routing from rendered stream objects
    const hasPersistedSourceManifest = Boolean(item.sources?.items?.length);
    const renderableStreamObjects = effectiveStreamObjects.filter(
      so =>
        so.type !== 'usage' &&
        so.type !== 'meta' &&
        so.type !== 'warning' &&
        so.type !== 'routing' &&
        !(hasPersistedSourceManifest && isSourceToolResultStreamObject(so))
    );
    const shouldShowProgress = shouldShowAssistantProgressPlaceholder({
      isLast,
      status,
      content,
      streamObjects: renderableStreamObjects,
    });

    return html`
      ${routingObject && routingObject.type === 'routing'
        ? html`<span
            class="routing-badge"
            title="Auto-routed: ${routingObject.complexity} (score ${routingObject.score})"
            >Auto · ${routingObject.resolvedModel}</span
          >`
        : nothing}
      ${warningObject && warningObject.type === 'warning'
        ? this.renderBudgetWarningBanner(warningObject)
        : nothing}
      ${this.renderImages()}
      ${shouldShowProgress
        ? html`<ai-loading
            data-testid="assistant-stream-progress"
          ></ai-loading>`
        : renderableStreamObjects.length
          ? this.renderStreamObjects(renderableStreamObjects)
          : this.renderRichText(content)}
      ${this.state === 'finished' ? this.renderAssistantSources() : nothing}
      ${shouldRenderError
        ? AIChatErrorRenderer(error, host, this.onOpenSettings)
        : nothing}
      ${this.renderEditorActions()}
      ${metaObject && metaObject.type === 'meta' && this.onOpenSettings
        ? this.renderLowBalanceBanner(metaObject)
        : nothing}
    `;
  }

  /**
   * sl-canvas2-8.31 Phase 6: Render tool-approval-request as confirmation card
   */
  private renderToolApprovalRequest(toolData: ToolMessageData) {
    const toolName = toolData.toolCall?.name ?? 'Unknown Tool';
    const confirmationItem: ConfirmationMessage = {
      type: 'confirmation',
      id: toolData.approvalId ?? '',
      toolName,
      displayName: localizedToolName(toolName),
      description:
        toolData.description ??
        localizedToolText(
          'com.streamlain.ai.confirmation.execute-fallback',
          `Execute ${toolName}`,
          { tool: localizedToolName(toolName) }
        ),
      copy: parseLocalizedCopy(toolData.confirmationCopy),
      metadata: toolData.toolCall?.arguments as Record<string, unknown>,
    };

    return html`<chat-message-confirmation
      .item=${confirmationItem}
      .sessionId=${this.session?.sessionId}
      .readOnly=${true}
      .workspaceId=${this.workspaceId}
    ></chat-message-confirmation>`;
  }

  private renderImages() {
    const { item } = this;
    if (!item.attachments) return nothing;

    return html`<chat-content-images
      .images=${item.attachments}
    ></chat-content-images>`;
  }

  private renderStreamObjects(answer: StreamObject[]) {
    return html`<chat-content-stream-objects
      .host=${this.host}
      .std=${this.std}
      .answer=${answer}
      .state=${this.state}
      .width=${this.width}
      .extensions=${this.extensions}
      .affineFeatureFlagService=${this.affineFeatureFlagService}
      .notificationService=${this.notificationService}
      .theme=${this.themeSignal}
      .independentMode=${this.independentMode}
      .docDisplayService=${this.docDisplayService}
      .peekViewService=${this.peekViewService}
      .onOpenDoc=${this.onOpenDoc}
      .workspaceDocSyncEngine=${this.workspaceDocSyncEngine}
      .renderMode=${'markdown'}
      .autoExecuteCanvasResults=${this.isLast &&
      (this.status === 'loading' ||
        this.status === 'transmitting' ||
        this.status === 'success')}
    ></chat-content-stream-objects>`;
  }

  private renderRichText(text: string) {
    return html`<chat-content-rich-text
      .text=${text}
      .state=${this.state}
      .extensions=${this.extensions}
      .affineFeatureFlagService=${this.affineFeatureFlagService}
      .theme=${this.themeSignal}
      .renderMode=${'markdown'}
    ></chat-content-rich-text>`;
  }

  private renderAssistantSources() {
    const manifest = this.item.sources;
    if (!manifest?.items?.length) return nothing;
    const sources = assistantSourceManifestToPillItems(
      manifest,
      this.docDisplayService
    );
    if (!sources.length) return nothing;

    return renderSourceList(this, sources, '');
  }

  private getReusableActionStreamObjects(item: ChatMessage): StreamObject[] {
    const content = sanitizeChatMessageContentForDisplay(item.content);

    if (item.streamObjects?.length) {
      const hasTextContent = item.streamObjects.some(
        streamObject =>
          streamObject.type === 'text-delta' ||
          streamObject.type === 'reasoning'
      );

      if (hasTextContent) {
        return this.sanitizeStreamObjects(item.streamObjects);
      }

      return [
        ...item.streamObjects,
        ...(content.trim()
          ? [{ type: 'text-delta' as const, textDelta: content }]
          : []),
      ];
    }

    return this.reconstructStreamObjects(item);
  }

  private renderEditorActions() {
    if (this.readOnlyPresentation) return nothing;

    const { item, isLast, status, host, session } = this;

    if (!isChatMessage(item) || item.role !== 'assistant') return nothing;

    if (
      isLast &&
      status !== 'success' &&
      status !== 'idle' &&
      status !== 'error'
    )
      return nothing;

    const { content, id: messageId } = item;
    const streamObjects = this.getReusableActionStreamObjects(item);
    const rawMarkdown = mergeStreamContent(streamObjects) || content;
    const markdown = sanitizeChatMessageContentForDisplay(rawMarkdown);

    const actions = host
      ? isInsidePageEditor(host)
        ? PageEditorActions
        : EdgelessEditorActions
      : null;

    const imageAttachments = item.attachments ?? [];
    const payload: AssistantTurnPayload = {
      markdown,
      attachments: imageAttachments,
      streamObjects,
      sources: item.sources,
      sessionId: session?.sessionId,
      messageId: messageId ?? undefined,
    };
    const reusableMarkdown = buildReusableAssistantTurnMarkdown(payload);
    const codeProposalAction = isLast
      ? this.renderCodeProposalAction(markdown, messageId ?? undefined)
      : nothing;
    const showActions =
      host &&
      !this.independentMode &&
      (!!markdown || imageAttachments.length > 0);

    return html`
      ${codeProposalAction}
      <chat-copy-more
        .host=${host}
        .session=${session}
        .actions=${showActions ? actions : []}
        .content=${reusableMarkdown}
        .payload=${payload}
        .isLast=${isLast}
        .messageId=${messageId}
        .withMargin=${true}
        .retry=${() => this.retry()}
        .notificationService=${this.notificationService}
      ></chat-copy-more>
      ${isLast && showActions
        ? html`<chat-action-list
            .actions=${actions}
            .host=${host}
            .session=${session}
            .content=${markdown}
            .payload=${payload}
            .attachments=${imageAttachments}
            .streamObjects=${streamObjects}
            .messageId=${messageId ?? undefined}
            .withMargin=${true}
            .notificationService=${this.notificationService}
          ></chat-action-list>`
        : nothing}
    `;
  }

  private renderCodeProposalAction(markdown: string, messageId?: string) {
    const selection = this.codeSelections[0];
    if (!selection) return nothing;

    const proposal = extractCodeProposal(markdown, selection.language);
    if (!proposal) return nothing;

    const lines =
      selection.fromLine === selection.toLine
        ? `L${selection.fromLine}`
        : `L${selection.fromLine}-${selection.toLine}`;

    return html`<button
      type="button"
      class="code-proposal-action"
      data-testid="code-proposal-review-action"
      @click=${() => {
        dispatchCodeSelectionProposal({
          selectionId: selection.id,
          proposal,
          language: selection.language,
          sourceMessageId: messageId,
        });
      }}
    >
      ${CodeBlockIcon({ width: '16px', height: '16px' })} Diff prüfen ·
      ${selection.title} ${lines}
    </button>`;
  }

  /**
   * sl-k8wk.2.2: Render low-balance warning banner (Owner/Admin only).
   * Shows once per browser session (sessionStorage dedup).
   * Only rendered when onOpenSettings is provided (Owner/Admin context).
   */
  private renderLowBalanceBanner(meta: StreamObject & { type: 'meta' }) {
    const DEDUP_KEY = 'sl-ai-low-balance-shown';
    try {
      if (sessionStorage.getItem(DEDUP_KEY)) return nothing;
    } catch {
      // sessionStorage unavailable (e.g. iframe sandbox) — show anyway
    }

    const percent = meta.remainingPercent ?? 0;

    const dismiss = () => {
      try {
        sessionStorage.setItem(DEDUP_KEY, '1');
      } catch {
        // ignore
      }
      this.requestUpdate();
    };

    const openSettings = () => {
      dismiss();
      this.onOpenSettings?.();
    };

    return html`<div
      class="low-balance-banner"
      data-testid="low-balance-banner"
    >
      <span class="low-balance-banner-text">
        ${I18n.t('com.streamlain.ai.low-balance.text', {
          percent: percent < 1 ? '1' : String(Math.round(percent)),
        })}
      </span>
      <button class="low-balance-banner-action" @click=${openSettings}>
        ${I18n.t('com.streamlain.ai.low-balance.action')}
      </button>
      <button
        class="low-balance-banner-dismiss"
        @click=${dismiss}
        title=${I18n.t('com.streamlain.ai.low-balance.dismiss')}
      >
        ${CloseIcon()}
      </button>
    </div>`;
  }

  // sl-ufq6.7.1: Yellow warning banner for budget warn-mode
  private renderBudgetWarningBanner(
    warning: StreamObject & { type: 'warning' }
  ) {
    const resetAt = warning.resetAt
      ? new Date(warning.resetAt).toLocaleTimeString()
      : '';
    const spent = warning.spentMicro
      ? (Number(BigInt(warning.spentMicro)) / 10000).toFixed(0)
      : '?';
    const limit = warning.limitMicro
      ? (Number(BigInt(warning.limitMicro)) / 10000).toFixed(0)
      : '?';

    return html`<div
      class="low-balance-banner"
      style="background: var(--streamlain-background-warning-color, rgba(255, 152, 0, 0.1)); border: 1px solid var(--streamlain-warning-color, #f59e0b); color: var(--streamlain-warning-color, #f59e0b);"
      data-testid="budget-warning-banner"
    >
      <span class="low-balance-banner-text">
        ${I18n.t('com.streamlain.ai.limit.warn-approaching', {
          spent,
          limit,
        })}${resetAt ? ` (Reset: ${resetAt})` : ''}
      </span>
    </div>`;
  }

  protected override render() {
    const { isLast, status } = this;

    if (isLast && status === 'loading') {
      return html`<ai-loading></ai-loading>`;
    }

    return html`
      ${this.renderHeader()}
      <div class="item-wrapper">${this.renderContent()}</div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'chat-message-assistant': ChatMessageAssistant;
  }
}
